import { types, Instance, destroy, SnapshotIn, castToSnapshot } from 'mobx-state-tree';
import { callAgroApi } from '@services/agroapi';
import { PlaceResponse } from '@services/responseTypes';
import { Transformers } from '@services/transformer';
export const Place = types.model('Place', {
  id: types.identifier,
  name: types.string,
  address: types.string,
  kind: types.string,
  type: types.optional(types.string, 'Place'),
  annual_production_target: types.maybeNull(types.number),
  annual_production_target_unit: types.maybeNull(types.string),
  capacity: types.maybeNull(types.number),
  capacity_unit: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  phone: types.maybeNull(types.string),
  stats: types.optional(types.model({
    median_price: types.number,
    median_price_unit: types.string,
    production_received: types.number,
    production_received_unit: types.string
  }), {
    median_price: 0,
    median_price_unit: '',
    production_received: 0,
    production_received_unit: ''
  }),
  lnglat: types.model({
    type: types.string,
    coordinates: types.array(types.number)
  })
}).actions(self => ({
  updateFromApi(place: PlaceResponse) {
    self.name = place.name;
    self.description = place.description;
    self.phone = place.phone;
    self.type = place.type;
    self.address = place.address;
    self.lnglat = castToSnapshot(place.lnglat);
  },
  update(params: {
    name: string;
    phone: string;
    description: string;
    kind: string;
    type: string;
    address: string;
    annual_production_target: number;
    capacity: number;
    location: number[];
  }) {
    return callAgroApi<PlaceResponse>(`/places/${self.id}`, 'PATCH', params).then(({
      data
    }) => {
      this.updateFromApi(data);
      return self;
    });
  }
})).views(self => ({
  get option() {
    return {
      label: self.name,
      value: self.id
    };
  }
}));
export type IPlace = Instance<typeof Place>;
export type IPlaceSnapshotIn = SnapshotIn<typeof Place>;
export const PlaceStore = types.model('PlaceStore', {
  places: types.map(Place)
}).actions(self => ({
  pushToPlaces(place: PlaceResponse) {
    self.places.put(Transformers.place(place));
    return self.places.get(place.id);
  },
  removeFromPlaces(place: IPlace) {
    destroy(place);
  }
})).actions(self => ({
  fetchPlaces(orgId: string, page = 1) {
    return callAgroApi<PlaceResponse[]>(`/organizations/${orgId}/places?page=${page.toString()}`, 'GET').then(({
      data
    }) => data.map(p => self.pushToPlaces(p)));
  },
  fetchPlace(placeId: string) {
    return callAgroApi<PlaceResponse>(`/places/${placeId}`, 'GET').then(({
      data
    }) => self.pushToPlaces(data));
  },
  createPlace(orgId: string, params: {
    name: string;
    phone: string;
    description: string;
    location: number[];
  }) {
    return callAgroApi<PlaceResponse>(`/organizations/${orgId}/places`, 'POST', params).then(({
      data
    }) => self.pushToPlaces(data));
  },
  deletePlace(placeId: string) {
    return callAgroApi(`/places/${placeId}`, 'DELETE').then(() => {
      const place = self.places.get(placeId);
      if (place) destroy(place);
    });
  }
})).views(self => ({
  get destinationOptions() {
    return Array.from(self.places.values()).map(p => p.option);
  }
}));
export type IPlaceStore = Instance<typeof PlaceStore>;